// src/components/skills/SkillReferenceReader.tsx
//
// Leitor fullscreen de reference da skill (Reforma UX §6.7, SKL-03,
// wireframe dlgReader). Abre POR CIMA do modal de detalhes (o modal continua
// montado por baixo — "Fechar" volta pro modal).
//
// Render por tipo (extensão do nome):
//   .md            → ReactMarkdown (prose)
//   .html / .htm   → iframe sandbox com srcDoc (não executa script)
//   .pdf           → se o content for URL, <object>; senão fallback <pre>
//   resto          → <pre> monoespaçado
//
// NOTA: references_bundle guarda CONTEÚDO TEXTO inline (jsonb). PDF binário
// inline não existe nesse contrato — só renderizamos PDF quando o content é
// uma URL. Divergência reportada na entrega.

import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { NormalizedReference } from './skill-constants';

export interface SkillReferenceReaderProps {
  reference: NormalizedReference | null;
  open: boolean;
  onClose: () => void;
}

type ReaderKind = 'md' | 'html' | 'pdf' | 'text';

function detectKind(name: string): ReaderKind {
  const lower = name.toLowerCase();
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'md';
  if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'html';
  if (lower.endsWith('.pdf')) return 'pdf';
  return 'text';
}

function isUrl(content: string): boolean {
  return /^https?:\/\/\S+$/i.test(content.trim());
}

export function SkillReferenceReader({ reference, open, onClose }: SkillReferenceReaderProps) {
  if (!open || !reference) return null;

  const kind = detectKind(reference.name);

  // PORTAL pro body (SKL-D 2026-06-12): o leitor é TELA CHEIA (wireframe
  // dlgReader). Renderizado dentro do DialogContent o `position: fixed` vira
  // relativo ao transform do Radix (ficava confinado na modal). z acima do
  // Dialog (z-400) + pointer-events próprio (o Radix modal desliga no body).
  return createPortal(
    <div
      className="fixed inset-0 z-[600] bg-raios-bg flex flex-col"
      style={{ pointerEvents: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-label={`Leitor: ${reference.name}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-raios-border bg-raios-surface shrink-0">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-raios-accent/10 text-raios-accent-text text-[10px] font-mono uppercase">
          <FileText className="w-3 h-3" aria-hidden />
          {kind}
        </span>
        <span className="text-sm font-mono text-raios-text truncate flex-1">{reference.name}</span>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fechar leitor">
          <X className="w-4 h-4" />
          Fechar
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {kind === 'md' && (
          <div className="prose prose-sm dark:prose-invert max-w-3xl mx-auto break-words">
            <ReactMarkdown>{reference.content}</ReactMarkdown>
          </div>
        )}
        {kind === 'html' && (
          <iframe
            title={reference.name}
            sandbox=""
            srcDoc={reference.content}
            className="w-full h-full min-h-[70vh] bg-white rounded-lg border border-raios-border"
          />
        )}
        {kind === 'pdf' &&
          (isUrl(reference.content) ? (
            <object
              data={reference.content.trim()}
              type="application/pdf"
              className="w-full h-full min-h-[70vh] rounded-lg border border-raios-border"
            >
              <p className="text-sm text-raios-text-secondary">
                Não foi possível embutir o PDF.{' '}
                <a
                  href={reference.content.trim()}
                  target="_blank"
                  rel="noreferrer"
                  className="text-raios-accent-text hover:underline"
                >
                  Abrir em nova aba
                </a>
              </p>
            </object>
          ) : (
            <pre className="whitespace-pre-wrap font-mono text-xs text-raios-text-secondary max-w-3xl mx-auto">
              {reference.content}
            </pre>
          ))}
        {kind === 'text' && (
          <pre className="whitespace-pre-wrap font-mono text-xs text-raios-text-secondary max-w-3xl mx-auto">
            {reference.content}
          </pre>
        )}
      </div>
    </div>,
    document.body,
  );
}
